// prototypes/BudgetTrendGrid.tsx (v2.18)
// 「予算・前年比較」 タブ 2段目: 20指標 (4列×5行) の mini line chart。
// TrendGrid と同 style (緑 実線=実績 / 橙 点線=予算 / 灰 線=前年同月)、 派生指標 4 つも同じレイアウト。
import { useMemo } from "react";
import * as Plot from "@observablehq/plot";
import { PlotFigure } from "../Plot";
import { buildMonthAxisMark } from "../../lib/chartAxis";
import { movingAvgTrend, type ResolvedMetric } from "./budgetSpec";
import { isYenCardMetric, yenUseOku, fmtYenScaled, fmtYenAxisTick, ymJp } from "../../lib/format";

interface Props { resolved: ResolvedMetric[]; targetName: string; periodFromYM: string; asOfYM: string; avgMode?: boolean; }

// 月末未確定で 0 になる指標 (KpiGrid と同じ)
const SKIP_ZERO_KEYS = new Set([
  "GP", "GP単価", "GP単価_在院",
  "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費",
]);

export function BudgetTrendGrid({ resolved, targetName, periodFromYM, asOfYM, avgMode = false }: Props) {
  // Charts は tableOnly を除外して 18 表示 (6×3 grid)
  const chartRows = resolved.filter((r) => !r.spec.tableOnly);
  // 共通 x domain (全 metric で同じ trendMonths を使うため、 先頭/末尾だけ取れば OK)。
  // 直近12ヶ月平均モードは移動平均 trend (末尾13点) の範囲を使う。
  const xDomain = useMemo<[Date, Date] | undefined>(() => {
    const first = chartRows.find((r) => r.trend.length > 0);
    if (!first) return undefined;
    const t = avgMode ? movingAvgTrend(first.trend, 12) : first.trend;
    if (!t.length) return undefined;
    return [new Date(t[0].date), new Date(t[t.length - 1].date)];
  }, [chartRows, avgMode]);

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-base font-semibold">{targetName} 主要指標の{avgMode ? "12ヶ月移動平均推移" : "月次推移"}</h2>
        <span className="text-xs text-slate-500 hidden sm:inline">
          期間 {ymJp(periodFromYM)} 〜 {ymJp(asOfYM)} / 各 chart 共通 x 軸 / y 軸 自動調整
        </span>
        <div className="ml-auto flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1">
            <span style={{ width: 14, height: 2, background: "#1B9E77", display: "inline-block" }}></span>実績
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: 14, height: 0, borderTop: "2px dashed #d97706", display: "inline-block" }}></span>予算
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: 14, height: 0, borderTop: "1.5px solid #94a3b8", opacity: 0.7, display: "inline-block" }}></span>前年同月
          </span>
        </div>
      </div>
      {/* 2 列 (mobile) → 3 列 (md) → 6 列 (lg+) で 3 行 /
          print-6cols: 印刷時は viewport 幅に関わらず 6 列を強制 (A4 横) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 print-6cols">
        {chartRows.map((r) => (
          <BudgetTrendMini key={r.spec.key} r={r} xDomain={xDomain} avgMode={avgMode} />
        ))}
      </div>
    </section>
  );
}

function BudgetTrendMini({ r, xDomain, avgMode }: { r: ResolvedMetric; xDomain: [Date, Date] | undefined; avgMode: boolean }) {
  const { spec } = r;
  const skipZero = SKIP_ZERO_KEYS.has(spec.key);
  const isMissing = (v: number | null): boolean => v == null || (skipZero && v === 0);

  // 入院収入/粗利(GP) は カードと同じ 円(億/万、 期間内max判定) で表示 (2026-06-24)。
  const isYen = isYenCardMetric(spec.key, spec.unit);
  const yenOku = isYen && yenUseOku(Math.max(0, ...r.trend.map((p) => Math.abs(p.actual ?? 0))));
  const gScale = isYen ? 1000 : 1;                          // グラフ: 千円→円
  const yenUnit = yenOku ? "億円" : "万円";

  type Row = { date: Date; value: number; series: "実績" | "予算" | "前年同月" };
  type Wide = { date: Date; actual: number | null; budget: number | null; prevYear: number | null };
  const rows: Row[] = [];
  const wide: Wide[] = [];
  // 直近12ヶ月平均モードは 12ヶ月移動平均 trend (末尾13点) を描画。
  // skipZero 指標 (GP/単価/薬剤費) は予算未設定月の 0 を母数から除外 (予算線が不当に低くなるのを防ぐ)。
  const trend = avgMode ? movingAvgTrend(r.trend, 12, skipZero) : r.trend;
  trend.forEach((p) => {
    const date = new Date(p.date);
    const actual   = isMissing(p.actual)   ? null : p.actual;
    const budget   = isMissing(p.budget)   ? null : p.budget;
    const prevYear = isMissing(p.prevYear) ? null : p.prevYear;
    if (actual   != null) rows.push({ date, value: actual   * gScale, series: "実績" });
    if (budget   != null) rows.push({ date, value: budget   * gScale, series: "予算" });
    if (prevYear != null) rows.push({ date, value: prevYear * gScale, series: "前年同月" });
    wide.push({ date, actual, budget, prevYear });   // wide は 千円 のまま (tooltip 用)
  });

  const fmtVal = (v: number | null) =>
    v == null ? "—" :
    isYen ? (() => { const f = fmtYenScaled(v, yenOku); return `${f.value} ${f.unit}`; })() :
    spec.unit === "%" ? `${(v * 100).toFixed(1)}%` :
    spec.unit === "日" ? `${v.toFixed(1)} 日` :
    // 「人」 は派生指標 (在院患者延数/医師1名あたり 等) で 5.0 / 4.6 等の小数値が出るため小数 1 桁。
    // 整数値 (1074 等) は toFixed(1) で 1074.0 になるが許容、 または fmt1 で自動切替。
    spec.unit === "人" ? `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} 人` :
    `${Math.round(v).toLocaleString()}${spec.unit ? ` ${spec.unit}` : ""}`;

  const options: Plot.PlotOptions = {
    width: 460, height: 130,
    marginLeft: 50, marginRight: 6, marginTop: 6, marginBottom: 22,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "9px" },
    x: { label: null, type: "time", axis: null, ...(xDomain ? { domain: xDomain } : {}) },
    y: { label: null, grid: true, nice: true, zero: false,
         tickFormat: (v: number) =>
           isYen ? fmtYenAxisTick(v, yenOku) :
           spec.unit === "%" ? `${Math.round(v * 100)}%` :
           spec.unit === "日" ? v.toFixed(1) :
           // 「人」 の派生指標 (5.0 / 4.6 等) は y 軸も小数 1 桁にしないと刻みが潰れる
           spec.unit === "人" ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) :
           Math.round(v).toLocaleString(),
    },
    marks: [
      Plot.lineY(rows.filter((d) => d.series === "前年同月"), {
        x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.6,
      }),
      Plot.dot(rows.filter((d) => d.series === "前年同月"), {
        x: "date", y: "value", fill: "#94a3b8", fillOpacity: 0.6, r: 1.8,
      }),
      Plot.lineY(rows.filter((d) => d.series === "予算"), {
        x: "date", y: "value", stroke: "#d97706", strokeWidth: 1.8, strokeDasharray: "5 3",
      }),
      Plot.dot(rows.filter((d) => d.series === "予算"), {
        x: "date", y: "value", fill: "#d97706", r: 1.8,
      }),
      Plot.lineY(rows.filter((d) => d.series === "実績"), {
        x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.4,
      }),
      Plot.dot(rows.filter((d) => d.series === "実績"), {
        x: "date", y: "value", fill: "#1B9E77", r: 2.4,
      }),
      Plot.tip(wide, Plot.pointerX({
        x: "date",
        y: (d: Wide) => (d.actual ?? d.prevYear ?? d.budget ?? 0) * gScale,
        fontSize: 11,
        title: (d: Wide) => [
          `実績    : ${fmtVal(d.actual)}`,
          `予算    : ${fmtVal(d.budget)}`,
          `前年同月 : ${fmtVal(d.prevYear)}`,
        ].join("\n"),
      })),
      buildMonthAxisMark(xDomain),
    ].filter(Boolean) as Plot.Markish[],
  };

  return (
    <div className="border border-slate-200 rounded px-1.5 py-1 bg-slate-50/30 min-w-0">
      <div className="text-[11px] font-semibold text-slate-700 leading-tight">
        {spec.label}
        {(isYen ? yenUnit : spec.unit) && <span className="text-slate-500 font-normal"> ({isYen ? yenUnit : spec.unit})</span>}
      </div>
      <PlotFigure options={options} responsiveWidth />
    </div>
  );
}
